import * as THREE from 'three';

// ─── カウンターの中に立つマスター（ローポリ手作りキャラ）───

const mat = (color, roughness = 0.8) => new THREE.MeshStandardMaterial({ color, roughness });

function part(geo, material, x, y, z, parent) {
  const m = new THREE.Mesh(geo, material);
  m.position.set(x, y, z);
  m.castShadow = true;
  parent.add(m);
  return m;
}

// 頭上の「MASTER」ラベル
function makeNameSprite() {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 64;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = 'rgba(17, 21, 15, 0.65)';
  ctx.strokeStyle = 'rgba(184, 157, 90, 0.8)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  if (ctx.roundRect) {
    ctx.roundRect(48, 8, 160, 48, 24);
  } else {
    ctx.rect(48, 8, 160, 48); // 古いブラウザは角丸なしで代替
  }
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = '#ece5d8';
  ctx.font = '24px "Cormorant Garamond", serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('MASTER', 128, 34);

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false })
  );
  sprite.scale.set(0.6, 0.15, 1);
  return sprite;
}

/**
 * マスターをカウンターの中に配置する。
 * 返り値: { interactable, update(elapsed) }
 */
export function createBarista(scene) {
  const g = new THREE.Group();

  const skin = mat(0xd9a97c, 0.7);
  const shirt = mat(0xe8e0d0, 0.85);
  const apron = mat(0x1a3a2a, 0.9);
  const pants = mat(0x2b2620, 0.9);
  const hair = mat(0x3a2c1e, 0.9);

  // 脚・靴
  [-0.08, 0.08].forEach((x) => {
    part(new THREE.BoxGeometry(0.11, 0.5, 0.13), pants, x, 0.3, 0, g);
    part(new THREE.BoxGeometry(0.12, 0.06, 0.2), mat(0x1a140e), x, 0.03, 0.02, g);
  });

  // 胴体＋シャツ
  const torso = part(new THREE.BoxGeometry(0.36, 0.52, 0.2), shirt, 0, 0.82, 0, g);
  // 前掛け（エプロン）＋首ひも
  part(new THREE.BoxGeometry(0.3, 0.44, 0.022), apron, 0, 0.76, 0.112, g);
  part(new THREE.BoxGeometry(0.24, 0.3, 0.022), apron, 0, 0.42, 0.09, g); // 腰から下
  part(new THREE.BoxGeometry(0.05, 0.16, 0.02), apron, 0, 1.06, 0.11, g);
  // 腰ひも
  part(new THREE.BoxGeometry(0.38, 0.035, 0.22), mat(0x14281c), 0, 0.6, 0, g);

  // 腕（少し開いて自然に）
  const armL = part(new THREE.BoxGeometry(0.09, 0.42, 0.1), shirt, -0.24, 0.82, 0, g);
  armL.rotation.z = 0.12;
  const armR = part(new THREE.BoxGeometry(0.09, 0.42, 0.1), shirt, 0.24, 0.82, 0, g);
  armR.rotation.z = -0.12;
  // 手
  part(new THREE.SphereGeometry(0.05, 10, 8), skin, -0.27, 0.6, 0.01, g);
  part(new THREE.SphereGeometry(0.05, 10, 8), skin, 0.27, 0.6, 0.01, g);

  // 首・頭
  part(new THREE.CylinderGeometry(0.05, 0.06, 0.08, 10), skin, 0, 1.11, 0, g);
  const head = new THREE.Group();
  part(new THREE.SphereGeometry(0.13, 16, 14), skin, 0, 0, 0, head);
  // 短髪（上半球）＋もみあげ
  const hairCap = part(new THREE.SphereGeometry(0.135, 16, 10, 0, Math.PI * 2, 0, Math.PI * 0.45), hair, 0, 0.015, -0.01, head);
  hairCap.rotation.x = -0.25;
  // ひげ（あご全体を覆うしっかりした無精ひげ＋口ひげ）
  const beard = part(new THREE.SphereGeometry(0.115, 14, 10, 0, Math.PI, Math.PI * 0.42, Math.PI * 0.45), hair, 0, -0.015, 0.02, head);
  beard.scale.set(1.05, 1.1, 1.0);
  part(new THREE.BoxGeometry(0.16, 0.07, 0.06), hair, 0, -0.115, 0.07, head); // あご下のボリューム
  const mustache = part(new THREE.BoxGeometry(0.085, 0.022, 0.03), hair, 0, -0.045, 0.122, head); // 口ひげ
  mustache.rotation.x = 0.15;
  // 目
  [-0.05, 0.05].forEach((x) => {
    part(new THREE.SphereGeometry(0.012, 6, 6), mat(0x1a140e, 0.4), x, 0.02, 0.122, head);
  });
  // 眉
  [-0.05, 0.05].forEach((x) => {
    part(new THREE.BoxGeometry(0.035, 0.01, 0.01), hair, x, 0.06, 0.12, head);
  });
  head.position.set(0, 1.28, 0);
  g.add(head);

  // 手に持つカップ（磨き中の演出）
  const cup = part(new THREE.CylinderGeometry(0.04, 0.032, 0.05, 12), mat(0xf5f2eb, 0.35), 0.27, 0.63, 0.05, g);
  const cloth = part(new THREE.BoxGeometry(0.07, 0.02, 0.07), mat(0xd8cdb4, 1), -0.27, 0.64, 0.04, g);
  cloth.rotation.y = 0.4;

  // 名前ラベル
  const label = makeNameSprite();
  label.position.set(0, 1.62, 0);
  g.add(label);

  // カウンターの中（客側 +z を向く）。全体を一回り大きく（身長約1.75m）
  g.scale.setScalar(1.24);
  g.position.set(-1.5, 0, -3.95);
  scene.add(g);

  // 呼吸・首の揺れ・カップ磨きのアイドルアニメ
  const update = (elapsed) => {
    torso.scale.y = 1 + Math.sin(elapsed * 1.7) * 0.012;
    head.rotation.y = Math.sin(elapsed * 0.5) * 0.18;
    head.rotation.x = Math.sin(elapsed * 1.7) * 0.02;
    cup.rotation.y = elapsed * 0.8;
    cloth.position.y = 0.64 + Math.sin(elapsed * 3.1) * 0.008;
  };

  const interactable = {
    id: 'master',
    type: 'master',
    label: 'マスター',
    data: {},
    // 客側（カウンター手前）を近接判定の基準にする
    position: new THREE.Vector3(-1.5, 1.2, -2.9),
    setHighlight: () => {},
  };

  return { interactable, update };
}
